import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'cartReservationEndsAt';

function getOrCreateEndsAt(minutes) {
  const existing = localStorage.getItem(STORAGE_KEY);
  if (existing) return existing;

  const iso = new Date(Date.now() + minutes * 60 * 1000).toISOString();
  localStorage.setItem(STORAGE_KEY, iso);
  return iso;
}

class CartReservationTimer extends Component {
  static get observedAttributes() {
    return ['duration-minutes'];
  }

  constructor() {
    super();
    this._onCartUpdate = null;
    this._mo = null;
    this._initScheduled = false;
  }

  connectedCallback() {
    super.connectedCallback?.();

    // Watch for drawer morph replacing/resetting our countdown-timer subtree
    this._startObserver();

    // Run once on connect (but delayed)
    this._scheduleInit();

    // Re-run after any cart update (cart drawer re-render)
    this._onCartUpdate = () => this._scheduleInit();
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this._onCartUpdate) {
      document.removeEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
      this._onCartUpdate = null;
    }
    this._mo?.disconnect();
    this._mo = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'duration-minutes') {
      localStorage.removeItem(STORAGE_KEY);
      this._scheduleInit(true);
    }
  }

  _startObserver() {
    if (this._mo) return;

    this._mo = new MutationObserver(() => {
      // If morph replaced the inner countdown or reset attributes, re-init.
      const t = this.querySelector('countdown-timer');
      if (!t) return;

      const missingEndsAt = !t.getAttribute('ends-at');
      const isPaused = t.hasAttribute('paused');

      if (missingEndsAt || isPaused) {
        this._scheduleInit(true);
      }
    });

    this._mo.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['paused', 'ends-at'],
    });
  }

  _scheduleInit(force = false) {
    // Coalesce multiple triggers into one init pass.
    if (this._initScheduled) return;
    this._initScheduled = true;

    // We run init multiple times because Horizon may patch in waves.
    const run = () => this._init(force).finally(() => {});
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        run();
        setTimeout(run, 50);
        setTimeout(() => {
          run();
          this._initScheduled = false;
        }, 250);
      });
    });
  }

  async _init(force = false) {
    await customElements.whenDefined('countdown-timer');

    const timer = this.querySelector('countdown-timer');
    if (!timer) return;

    // Only skip if already initialized AND it still has the required state.
    if (!force && timer.dataset.initialized === 'true' && timer.getAttribute('ends-at') && !timer.hasAttribute('paused')) {
      return;
    }

    timer.dataset.initialized = 'true';

    const minutes = Number(this.getAttribute('duration-minutes') || '10');
    const endsAtIso = getOrCreateEndsAt(minutes);

    timer.setAttribute('ends-at', endsAtIso);
    timer.removeAttribute('paused');

    timer.addEventListener(
      'countdown-finished',
      () => {
        localStorage.removeItem(STORAGE_KEY);
        this.style.display = 'none';
      },
      { once: true }
    );
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
